(() => {
  if (customElements.get('pw-giftset-bundle')) return;
  class GiftsetBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.listenToCart(true);
        this.refreshCart();
        return;
      }
      this.initialized = true;
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.error = this.querySelector('[data-pw-bundle-error]');
      this.status = this.querySelector('[data-pw-bundle-status]');
      try {
        this.policy = JSON.parse(this.querySelector('[data-pw-bundle-data]').textContent);
        if (!this.policy?.gifts || !Array.isArray(this.policy.diffusers)) throw new Error('Missing offer configuration');
      }
      catch (_) { this.policy = { gifts: {}, diffusers: [], items: [] }; }
      this.messages = JSON.parse(this.querySelector('[data-pw-bundle-i18n]').textContent);
      this.cartItems = this.policy.items || [];
      this.onCartChange = event => {
        if (!Array.isArray(event.detail?.cart?.items)) return;
        this.cartItems = event.detail.cart.items;
        this.cartCodes = (event.detail.cart.discount_codes || []).filter(code => code.applicable).map(code => code.code);
        this.update();
      };
      this.onCartRefresh = () => this.refreshCart();
      this.onPageShow = event => { if (event.persisted) this.refreshCart(); };
      this.onVisible = () => { if (document.visibilityState === 'visible') this.refreshCart(); };
      this.listenToCart(true);
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.add();
      });
      this.form.addEventListener('change', () => this.update());
      // Quantity controls can be outside the form but associated through form=id.
      const externalQuantity = this.form.elements.namedItem('quantity');
      this.quantity = externalQuantity || this.form.elements.namedItem('pw-quantity');
      const localQuantity = this.querySelector('[data-pw-bundle-quantity]');
      if (localQuantity) localQuantity.hidden = !!externalQuantity;
      this.quantity?.addEventListener('input', () => this.update());
      this.quantity?.addEventListener('change', () => this.update());
      const selected = this.querySelector('[name="pw-finish"]:checked');
      if (selected?.disabled) {
        const available = this.querySelector('[name="pw-finish"]:not(:disabled)');
        if (available) available.checked = true;
      }
      this.update();
    }
    disconnectedCallback() {
      this.listenToCart(false);
      clearTimeout(this.quoteTimer);
      this.quoteVersion = (this.quoteVersion || 0) + 1;
      this.quoteQueue = (this.quoteQueue || Promise.resolve()).catch(() => {}).then(() => this.clearQuoteCart()).catch(() => {});
    }
    listenToCart(connect) {
      const method = connect ? 'addEventListener' : 'removeEventListener';
      ['cart:change', 'pw:cart-reconciled'].forEach(name => document.documentElement[method](name, this.onCartChange));
      document[method]('cart:refresh', this.onCartRefresh);
      document[method]('visibilitychange', this.onVisible);
      window[method]('pageshow', this.onPageShow);
    }
    async refreshCart() {
      if (this.refreshing || this.busy) return;
      this.refreshing = true;
      try {
        const cart = await this.json(`${window.Shopify.routes.root}cart.js`);
        if (Array.isArray(cart.items) && this.isConnected && !this.busy) {
          this.cartItems = cart.items;
          this.requestedQuoteKey = null;
          this.cartCodes = (cart.discount_codes || []).filter(code => code.applicable).map(code => code.code);
          this.update();
          if (this.native) await this.refreshQuote();
        }
      } catch (_) { /* Retain last known display; add() still validates actual cart prices. */ }
      finally { this.refreshing = false; }
    }
    get bundled() { return this.form.elements.namedItem('pw-purchase').value === 'bundle'; }
    get diffuserPage() { return this.dataset.productKind === 'diffuser'; }
    get giftSelection() { return this.querySelector('[name="pw-gift"]:checked'); }
    get giftId() { return Number(this.giftSelection?.value || this.dataset.giftId); }
    get giftPrice() { return Number(this.giftSelection?.dataset.price || this.dataset.giftPrice); }
    get diffuserId() { return Number(this.diffuserPage ? this.dataset.diffuserId : this.finish?.value); }
    get mainPrice() { return this.diffuserPage ? Number(this.dataset.diffuserPrice) : this.giftPrice; }
    get giftAvailable() { return this.diffuserPage ? this.giftSelection?.dataset.available === 'true' : this.dataset.available === 'true'; }
    get finish() { return this.querySelector('[name="pw-finish"]:checked'); }
    get count() { return Number(this.quantity?.value || 1); }
    get diffuserRegularPrice() { return Number(this.diffuserPage ? this.dataset.diffuserPrice : this.finish?.dataset.price); }
    get native() { return this.policy.engine === 'native'; }
    get nativeProduct() {
      return (this.policy.nativeProducts || []).find(product => product.gift_variant_id === this.giftId && product.diffuser_variant_id === this.diffuserId);
    }
    get bundleUnitPrice() { return this.native ? this.nativeProduct?.price : this.giftPrice + this.policy.diffuserPrice; }
    get nativeEligible() { return this.policy.enabled === true && this.dataset.ready === 'true' && this.dataset.currency === 'EUR' && this.nativeProduct?.valid === true && this.nativeProduct?.available === true && Number.isInteger(this.nativeProduct?.price) && this.nativeProduct.price > 0; }
    quoteInput() {
      const lines = this.cartItems.map(line => {
        const plan = line.selling_plan_id || line.selling_plan_allocation?.selling_plan?.id;
        return { merchandiseId: `gid://shopify/ProductVariant/${line.variant_id}`, quantity: line.quantity,
          attributes: Object.entries(line.properties || {}).filter(([, value]) => value !== null).map(([key, value]) => ({ key, value: String(value) })),
          ...(plan ? { sellingPlanId: `gid://shopify/SellingPlan/${plan}` } : {}) };
      });
      return { lines, country: this.policy.country, codes: this.cartCodes || this.policy.discountCodes || [], variant: this.nativeProduct?.variant_id, price: this.nativeProduct?.price, quantity: this.count };
    }
    quoteKey() { return JSON.stringify(this.quoteInput()); }
    get policyValid() {
      return Number.isInteger(this.policy.diffuserPrice) && this.policy.diffuserPrice >= 0 &&
        Number.isInteger(this.policy.bps) && this.policy.bps >= 0 && this.policy.bps <= 10000 &&
        Number.isInteger(this.policy.minimum) && this.policy.minimum >= 1;
    }
    bundleGroups(items) {
      if (this.native) return items.flatMap(line => {
        const product = (this.policy.nativeProducts || []).find(product => product.valid && product.variant_id === line.variant_id);
        return product && !line.selling_plan_allocation && Number.isInteger(line.quantity) && line.quantity > 0
          ? [{ parent: line, product, quantity: line.quantity }] : [];
      });
      const groups = new Map();
      items.forEach(item => {
        const id = item.properties?._pw_giftset_bundle;
        if (!id || item.selling_plan_allocation) return;
        if (!groups.has(id)) groups.set(id, []);
        groups.get(id).push(item);
      });
      return [...groups.values()].flatMap(lines => {
        if (lines.length !== 2) return [];
        const gift = lines.find(line => this.policy.gifts[line.variant_id] > 0);
        const diffuser = lines.find(line => this.policy.diffusers.includes(line.variant_id));
        return gift && diffuser && Number.isInteger(gift.quantity) && Number.isInteger(diffuser.quantity) && gift.quantity > 0 && diffuser.quantity > 0
          ? [{ gift, diffuser, quantity: Math.min(gift.quantity, diffuser.quantity) }] : [];
      });
    }
    discounted(price, bps) { return price - Math.floor(price * bps / 10000); }
    selectionPrice(count) {
      if (this.bundled) {
        const gross = this.bundleUnitPrice * count;
        if (this.native) return { gross, total: this.nativeQuote?.key === this.quoteKey() ? this.nativeQuote.total : gross, bps: 0 };
        const inCart = this.bundleGroups(this.cartItems).reduce((sum, group) => sum + group.quantity, 0);
        const bps = count + inCart >= this.policy.minimum ? this.policy.bps : 0;
        return { gross, total: this.native ? this.discounted(this.bundleUnitPrice * count, bps) : (this.discounted(this.giftPrice, bps) + this.discounted(this.policy.diffuserPrice, bps)) * count, bps };
      }
      const ids = (this.dataset.pwDiscountProductIds || '').split('|');
      const inCart = this.cartItems.filter(item => !item.selling_plan_allocation && ids.includes(String(item.product_id))).reduce((sum, item) => sum + item.quantity, 0);
      const bps = this.dataset.pwDiscountEnabled === 'true' && count + inCart >= Number(this.dataset.pwDiscountMinimum) ? Number(this.dataset.pwDiscountBps) : 0;
      return { gross: this.mainPrice * count, total: this.discounted(this.mainPrice, bps) * count, bps };
    }
    get available() {
      if (this.bundled && this.native) return this.nativeEligible && this.nativeQuote?.key === this.quoteKey();
      return this.dataset.available === 'true' && (!this.bundled || (
        this.dataset.ready === 'true' && this.dataset.priceValid === 'true' && this.policyValid &&
        this.diffuserRegularPrice >= this.policy.diffuserPrice &&
        this.giftAvailable && (this.diffuserPage
          ? this.giftSelection?.dataset.priceValid === 'true'
          : this.finish?.dataset.available === 'true')
      ));
    }
    text(key, values = {}) {
      return (this.messages[key] || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => values[name] ?? '');
    }
    money(cents) {
      return new Intl.NumberFormat(this.dataset.locale || 'en-IE', { style: 'currency', currency: this.dataset.currency, maximumFractionDigits: 2 }).format(cents / 100);
    }
    update() {
      const count = Number.isInteger(this.count) && this.count > 0 ? this.count : 1;
      this.querySelector('.pw-bundle__details').hidden = !this.bundled;
      this.button.disabled = this.busy || this.locked || !this.available;
      const soldOut = this.bundled && this.native ? this.nativeProduct?.available === false : this.dataset.available !== 'true' || (this.bundled && !this.giftAvailable);
      this.querySelector('[data-pw-bundle-label]').textContent = this.text(soldOut ? 'sold_out' : !this.available ? 'unavailable' : this.bundled ? 'add_bundle' : this.diffuserPage ? 'add_diffuser' : 'add_gift');
      const price = this.selectionPrice(count);
      this.querySelector('[data-pw-bundle-price]').textContent = Number.isFinite(price.total) ? this.money(price.total) : '—';
      const compare = this.querySelector('[data-pw-bundle-compare]');
      compare.hidden = !(price.total < price.gross);
      compare.textContent = compare.hidden ? '' : this.money(price.gross);
      this.querySelectorAll('[data-pw-bundle-option-price], [data-pw-bundle-total]').forEach(el => el.textContent = this.policyValid && Number.isFinite(this.bundleUnitPrice) ? this.money(this.bundleUnitPrice) : '—');
      this.querySelectorAll('[data-pw-component-pricing]').forEach(el => el.hidden = this.native);
      this.querySelectorAll('[data-pw-gift-price]').forEach(el => el.textContent = this.money(this.giftPrice));
      this.querySelectorAll('[data-pw-diffuser-regular]').forEach(el => el.textContent = this.money(this.diffuserRegularPrice));
      const saving = this.giftPrice + this.diffuserRegularPrice - this.bundleUnitPrice;
      this.querySelectorAll('[data-pw-bundle-saving]').forEach(el => { const ok = this.policyValid && Number.isFinite(saving); el.textContent = ok ? this.money(Math.max(0, saving)) : ''; el.closest('.pw-bundle__saving')?.toggleAttribute('hidden', !ok); });
      const tier = this.querySelector('[data-pw-bundle-tier]');
      tier.hidden = this.native || !this.bundled || !this.policyValid || !this.policy.bps;
      tier.textContent = this.text('tier', { percent: this.policy.bps / 100, minimum: this.policy.minimum });
      this.status.textContent = this.bundled && this.native ? (!this.available ? this.text('combination_unavailable') : '') : this.dataset.available !== 'true'
        ? this.text(this.diffuserPage ? 'restock_diffuser' : 'restock_gift')
        : this.bundled && !this.giftAvailable ? this.text('set_sold_out')
        : !this.available ? this.text('combination_unavailable') : '';
      // The theme has external sticky submit buttons associated through form=id.
      // Mirror the selected offer so they cannot advertise the standalone price for a bundle.
      Array.from(this.form.elements).filter(el => el.type === 'submit' && el !== this.button).forEach(button => {
        button.disabled = this.button.disabled;
        const content = button.contentElement || button;
        const prices = document.createElement('span');
        prices.className = 'pw-bundle__prices';
        prices.append(compare.cloneNode(true), this.querySelector('[data-pw-bundle-price]').cloneNode(true));
        content.replaceChildren(`${this.querySelector('[data-pw-bundle-label]').textContent} - `, prices);
        if (this.busy) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
        button.closest('product-quick-add')?.toggleAttribute('data-pw-bundle-selected', this.bundled);
      });
      if (this.native && this.bundled && this.nativeEligible && !this.busy && this.requestedQuoteKey !== this.quoteKey()) {
        this.requestedQuoteKey = this.quoteKey();
        clearTimeout(this.quoteTimer);
        this.quoteTimer = setTimeout(() => this.obtainQuote().catch(() => {}), 250);
      }
    }
    async storefront(query, variables) {
      const body = await this.json('/api/2026-10/graphql.json', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query, variables }) });
      const result = Object.values(body.data || {})[0];
      if (body.errors?.length || !result || result.userErrors?.length ) throw new Error(this.text('offer_unavailable'));
      return result;
    }
    async clearQuoteCart() {
      if (!this.quoteCartId) return;
      if (this.quoteCartDirty && !this.quoteLineIds?.length) {
        const cart = await this.storefront('query($id:ID!){cart(id:$id){lines(first:100){nodes{id}}}}', { id: this.quoteCartId });
        this.quoteLineIds = cart.lines.nodes.map(line => line.id);
      }
      if (!this.quoteLineIds?.length) { this.quoteCartDirty = false; return; }
      await this.storefront('mutation($id:ID!,$lines:[ID!]!){cartLinesRemove(cartId:$id,lineIds:$lines){cart{id} userErrors{message}}}', { id: this.quoteCartId, lines: this.quoteLineIds });
      this.quoteLineIds = [];
      this.quoteCartDirty = false;
    }
    showQuoteRetry() {
      this.error.hidden = false;
      this.error.textContent = this.text('offer_unavailable') + ' ';
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'link';
      retry.textContent = this.text('retry');
      retry.addEventListener('click', () => {
        this.error.hidden = true;
        this.requestedQuoteKey = null;
        this.update();
      }, { once: true });
      this.error.append(retry);
    }
    async obtainQuote() {
      const key = this.quoteKey(), input = this.quoteInput(), version = this.quoteVersion = (this.quoteVersion || 0) + 1;
      const task = async () => {
        if (!this.isConnected || version !== this.quoteVersion) return;
        try {
          if (!this.nativeEligible || !/^[A-Z]{2}$/.test(input.country) || input.lines.length >= 100) throw new Error(this.text('offer_unavailable'));
          await this.clearQuoteCart();
          if (!this.quoteCartId) {
            const created = await this.storefront('mutation($input:CartInput!){cartCreate(input:$input){cart{id} userErrors{message}}}', { input: { buyerIdentity: { countryCode: input.country }, discountCodes: input.codes } });
            this.quoteCartId = created.cart?.id;
          } else {
            await this.storefront('mutation($id:ID!,$buyer:CartBuyerIdentityInput!){cartBuyerIdentityUpdate(cartId:$id,buyerIdentity:$buyer){cart{id} userErrors{message}}}', { id: this.quoteCartId, buyer: { countryCode: input.country } });
            await this.storefront('mutation($id:ID!,$codes:[String!]!){cartDiscountCodesUpdate(cartId:$id,discountCodes:$codes){cart{id} userErrors{message}}}', { id: this.quoteCartId, codes: input.codes });
          }
          const marker = crypto.randomUUID();
          const lines = [...input.lines, { merchandiseId: `gid://shopify/ProductVariant/${input.variant}`, quantity: input.quantity, attributes: [{ key: '_pw_giftset_bundle', value: marker }] }];
          this.quoteCartDirty = true;
          const result = await this.storefront('mutation($id:ID!,$lines:[CartLineInput!]!){cartLinesAdd(cartId:$id,lines:$lines){cart{lines(first:100){nodes{id quantity attributes{key value} merchandise{... on ProductVariant{id}} cost{totalAmount{amount currencyCode}}}}} userErrors{message} warnings{code}}}', { id: this.quoteCartId, lines });
          const returned = result.cart?.lines?.nodes || [];
          this.quoteLineIds = returned.map(line => line.id);
          if (result.warnings?.length || returned.reduce((sum, line) => sum + line.quantity, 0) !== lines.reduce((sum, line) => sum + line.quantity, 0)) throw new Error(this.text('offer_unavailable'));
          const selected = returned.filter(line => line.attributes.some(attribute => attribute.key === '_pw_giftset_bundle' && attribute.value === marker));
          if (selected.length !== 1 || selected[0].merchandise.id !== `gid://shopify/ProductVariant/${input.variant}` || selected[0].quantity !== input.quantity || selected[0].cost.totalAmount.currencyCode !== 'EUR') throw new Error(this.text('offer_unavailable'));
          const total = Math.round(Number(selected[0].cost.totalAmount.amount) * 100);
          if (!Number.isSafeInteger(total) || total < 0) throw new Error(this.text('offer_unavailable'));
          if (this.isConnected && version === this.quoteVersion && key === this.quoteKey()) this.nativeQuote = { key, total };
        } catch (error) {
          if (!this.quoteCartDirty && !this.quoteLineIds?.length) this.quoteCartId = null;
          if (version === this.quoteVersion) {
            this.nativeQuote = null;
            if (!this.busy && this.isConnected) this.showQuoteRetry();
          }
          throw error;
        } finally {
          try { await this.clearQuoteCart(); }
          catch (error) { this.nativeQuote = null; if (!this.busy && this.isConnected) this.showQuoteRetry(); throw error; }
          finally { if (this.isConnected) this.update(); }
        }
      };
      this.quoteQueue = (this.quoteQueue || Promise.resolve()).catch(() => {}).then(task);
      return this.quoteQueue;
    }
    async json(url, options) {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options });
      const body = await response.json();
      if (!response.ok) throw new Error(body.description || this.text('retry'));
      return body;
    }
    async present(cart, sections, root, addedItems) {
      const drawer = document.querySelector('cart-drawer');
      const sectionId = drawer?.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
      if (!drawer || !sectionId) throw new Error(this.text('cart_unconfirmed'));
      const parse = html => {
        if (typeof html !== 'string') return null;
        const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('cart-drawer');
        return parsed?.querySelector('.cart-drawer__inner') && parsed.querySelector('[slot="footer"]') ? parsed : null;
      };
      let markup = parse(sections?.[sectionId]);
      if (!markup) {
        sections = await this.json(`${root}?sections=${encodeURIComponent(sectionId)}`);
        markup = parse(sections[sectionId]);
      }
      if (!markup) throw new Error(this.text('cart_unconfirmed'));
      cart.sections = sections;
      // Render before notifying listeners: never show stale cart contents.
      drawer.replaceChildren(...markup.childNodes);
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart } }));
      this.form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: addedItems, cart } }));
      await customElements.whenDefined('cart-drawer');
      drawer.show();
    }
    async refreshQuote() {
      // The section is rendered with current Shopify prices, stock and shared policy,
      // including when a customer returns to a tab that was open before an update.
      const sectionId = this.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '');
      if (!sectionId) throw new Error(this.text('offer_unavailable'));
      const url = new URL(location.href);
      url.searchParams.set('sections', sectionId);
      url.searchParams.set('_pw_quote', Date.now());
      const sections = await this.json(url.toString());
      const doc = new DOMParser().parseFromString(sections[sectionId] || '', 'text/html');
      const fresh = [...doc.querySelectorAll('pw-giftset-bundle')].find(el => el.dataset.productKind === this.dataset.productKind && (this.diffuserPage ? el.dataset.diffuserId === this.dataset.diffuserId : el.dataset.giftId === this.dataset.giftId));
      if (!fresh) throw new Error(this.text('offer_unavailable'));
      const pricingState = () => JSON.stringify([this.policy.engine, this.policy.enabled, this.policy.nativeProducts, this.dataset.ready, this.dataset.available]);
      const before = pricingState();
      this.policy = JSON.parse(fresh.querySelector('[data-pw-bundle-data]').textContent);
      for (const key of ['ready', 'available', 'giftPrice', 'diffuserPrice', 'priceValid']) {
        if (fresh.dataset[key] !== undefined) this.dataset[key] = fresh.dataset[key];
      }
      for (const input of this.querySelectorAll('[name="pw-gift"], [name="pw-finish"]')) {
        const current = [...fresh.querySelectorAll(`[name="${input.name}"]`)].find(candidate => candidate.value === input.value);
        if (current) Object.assign(input.dataset, current.dataset);
      }
      this.cartItems = this.policy.items || [];
      this.update();
      // Changes are shown before a purchase; a second deliberate click accepts the new quote.
      const after = pricingState();
      if (before !== after || !this.nativeEligible) throw new Error(this.text('offer_unavailable'));
    }
    nativeCartValid(cart, group, quantity, variantId) {
      const groups = this.bundleGroups(cart.items);
      const added = groups.filter(pair => pair.parent.properties?._pw_giftset_bundle === group);
      if (cart.currency !== 'EUR' || added.length !== 1 || added[0].parent.variant_id !== variantId || added[0].quantity !== quantity) return false;
      return added.every(pair => {
        const line = pair.parent;
        const components = line.item_components || [];
        // Shopify may provide the authoritative final total on the parent or its components.
        const actual = line.final_line_price ?? (components.length ? components.reduce((sum, component) => sum + component.final_line_price, 0) : NaN);
        const expected = this.purchaseQuoteTotal;
        const componentIds = components.map(component => component.variant_id).sort();
        const allowedIds = [pair.product.gift_variant_id, pair.product.diffuser_variant_id].sort();
        return Number.isInteger(actual) && actual >= 0 && actual <= expected && (!components.length || (JSON.stringify(componentIds) === JSON.stringify(allowedIds) && components.every(component => component.quantity === pair.quantity)));
      });
    }
    async add() {
      if (this.busy || this.locked || !this.available || !this.form.reportValidity()) return;
      const quantity = this.count;
      if (!Number.isInteger(quantity) || quantity < 1) return;
      this.busy = true;
      this.error.hidden = true;
      this.button.setAttribute('aria-busy', 'true');
      this.update();
      const root = window.Shopify.routes.root;
      const bundled = this.bundled;
      const giftId = this.giftId;
      const diffuserId = this.diffuserId;
      const group = crypto.randomUUID();
      const properties = bundled ? { _pw_giftset_bundle: group } : {};
      let items = bundled
        ? [{ id: giftId, quantity, properties }, { id: diffuserId, quantity, properties }]
        : [{ id: this.diffuserPage ? diffuserId : giftId, quantity, properties }];
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      let writeStarted = false;
      let accepted = false;
      try {
        if (bundled && this.native) {
          const displayedTotal = this.nativeQuote?.total;
          await this.refreshQuote();
          await this.obtainQuote();
          if (!this.nativeQuote || this.nativeQuote.total !== displayedTotal) throw new Error(this.text('offer_unavailable'));
          this.purchaseQuoteTotal = this.nativeQuote.total;
          if (!this.bundled || this.giftId !== giftId || this.diffuserId !== diffuserId || this.count !== quantity) throw new Error(this.text('offer_unavailable'));
          if (this.native) items = [{ id: this.nativeProduct.variant_id, quantity, properties }];
        }
        writeStarted = true;
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items, sections: [...new Set(sections)].slice(0, 5), sections_url: location.pathname })
        });
        accepted = response.ok;
        const result = await response.json();
        if (!accepted) {
          writeStarted = false;
          throw new Error(result.description || this.text('add_failed'));
        }
        const cart = await this.json(`${root}cart.js`);
        const containsAddedItems = items.every(requested => cart.items.some(line =>
          line.variant_id === requested.id && line.quantity >= requested.quantity &&
          (!bundled || line.properties?._pw_giftset_bundle === group)
        ));
        if (!containsAddedItems) throw new Error(this.text('cart_unconfirmed'));
        if (bundled && this.native && !this.nativeCartValid(cart, group, quantity, items[0].id)) {
          const added = cart.items.filter(item => item.properties?._pw_giftset_bundle === group);
          const updates = Object.fromEntries(added.map(item => [item.key, 0]));
          if (Object.keys(updates).length) await this.json(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          accepted = false;
          writeStarted = false;
          throw new Error(this.text('offer_unavailable'));
        }
        if (bundled && !this.native) {
          const added = cart.items.filter(item => item.properties?._pw_giftset_bundle === group);
          const gift = added.find(item => item.variant_id === giftId);
          const diffuser = added.find(item => item.variant_id === diffuserId);
          const groups = this.bundleGroups(cart.items);
          const bundleCount = groups.reduce((sum, pair) => sum + pair.quantity, 0);
          const bps = bundleCount >= this.policy.minimum ? this.policy.bps : 0;
          const correctPrices = groups.every(pair => {
            const giftPrice = this.policy.gifts[pair.gift.variant_id];
            const extraDiffusers = pair.diffuser.quantity - pair.quantity;
            const expectedGift = this.discounted(giftPrice, bps) * pair.quantity + giftPrice * (pair.gift.quantity - pair.quantity);
            const expectedDiffuser = this.discounted(this.policy.diffuserPrice, bps) * pair.quantity + (extraDiffusers ? pair.diffuser.original_price * extraDiffusers : 0);
            return (pair.gift.final_line_price ?? pair.gift.final_price * pair.gift.quantity) === expectedGift &&
              (pair.diffuser.final_line_price ?? pair.diffuser.final_price * pair.diffuser.quantity) === expectedDiffuser;
          });
          if (cart.currency !== 'EUR' || added.length !== 2 || gift?.quantity !== quantity || diffuser?.quantity !== quantity || !groups.some(pair => pair.gift === gift && pair.diffuser === diffuser) || !correctPrices || cart.cart_level_discount_applications?.length) {
            // Remove only this attempt's uniquely identified lines; preserve the existing cart.
            const updates = Object.fromEntries(added.map(item => [item.key, 0]));
            if (Object.keys(updates).length) await this.json(`${root}cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            accepted = false;
            writeStarted = false;
            throw new Error(this.text('offer_unavailable'));
          }
        }
        await this.present(cart, result.sections, root, result.items || [result]);
      } catch (error) {
        this.error.hidden = false;
        if (accepted || writeStarted) {
          // An uncertain response must not cause a second add. Let the customer inspect the cart.
          this.locked = true;
          this.error.textContent = accepted ? this.text('added_check') : this.text('uncertain_check');
          const link = document.createElement('a');
          link.href = `${root}cart`;
          link.textContent = ' ' + this.text('view_cart');
          link.className = 'link';
          this.error.append(link);
        } else this.error.textContent = error.message;
      } finally {
        this.busy = false;
        if (this.native && bundled && !this.nativeQuote && !this.locked) this.showQuoteRetry();
        this.button.removeAttribute('aria-busy');
        this.update();
      }
    }
  }
  customElements.define('pw-giftset-bundle', GiftsetBundle);
})();
